import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProject, PERSONA_TYPE_LABELS, type Project, type RoleSession } from '../api/projects';
import { AppLayout } from '../components/AppLayout';
import { BigButton, EmptyState, LoadingDots } from '../components/ChildFriendly';

const MAX_PROBLEM_LENGTH = 500;
const MIN_PROBLEM_LENGTH = 10;

// 9 yaşına uygun, günlük hayattan örnekler — tıklanınca textareayı doldurur.
const EXAMPLE_PROBLEMS = [
  { icon: '🍱', text: 'Okul kantininde yemekler çok pahalı, öğrenciler dışarıdan yemek getirmek zorunda kalıyor.' },
  { icon: '🗑️', text: 'Okul bahçesinde çok çöp var ve kimse toplamıyor, oyun alanları pis kalıyor.' },
  { icon: '🚰', text: 'Tuvaletlerde sıra çok uzun ve sabun bazen bitiyor, ellerimizi yıkayamıyoruz.' },
  { icon: '📚', text: 'Kütüphanedeki kitaplar çok eski ve yeni kitaplar gelmiyor, sevecek kitap bulamıyoruz.' },
];

function personaIcon(rs: RoleSession): string {
  if (rs.persona_type && PERSONA_TYPE_LABELS[rs.persona_type]) {
    return PERSONA_TYPE_LABELS[rs.persona_type].icon;
  }
  return '👤';
}

export default function NewProjectPage() {
  const navigate = useNavigate();
  const [problemText, setProblemText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [createdProject, setCreatedProject] = useState<Project | null>(null);

  const trimmedLength = problemText.trim().length;
  const remaining = MAX_PROBLEM_LENGTH - problemText.length;
  const canSubmit = !submitting && trimmedLength >= MIN_PROBLEM_LENGTH;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const project = await createProject(problemText.trim());
      setCreatedProject(project);
    } catch (err) {
      const detail = (
        err as { response?: { data?: { detail?: string } } }
      )?.response?.data?.detail;
      setError(detail || 'Proje oluşturulamadı. Lütfen tekrar dene.');
    } finally {
      setSubmitting(false);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Görünüm 2: Proje oluşturuldu — önerilen roller
  // ──────────────────────────────────────────────────────────────
  if (createdProject) {
    return (
      <AppLayout
        title="Projen Hazır"
        subtitle="Yapay zekâ problemini inceledi ve uygun muhatapları seçti."
        width="narrow"
      >
        <div className="stack">
          <div className="hero">
            <span className="hero__badge">✨ Harika iş çıkardın</span>
            <h2 className="hero__title">Harika! Projen hazır.</h2>
            <p className="hero__subtitle">
              Aşağıdaki kişilerle tek tek konuşarak çözümünü anlatabilirsin.
            </p>
          </div>

          <div className="stepper" aria-label="İlerleme">
            <div className="step done">
              <span className="step-dot" aria-hidden="true">✓</span>
              <span className="step-label">Problemi Anlat</span>
            </div>
            <div className="step-line done" aria-hidden="true" />
            <div className="step active">
              <span className="step-dot" aria-hidden="true">2</span>
              <span className="step-label">Muhatapları Seç</span>
            </div>
          </div>

          <div className="problem-recap" aria-label="Problemin">
            <span className="problem-recap__label">
              <span aria-hidden="true">📝</span> Problemin
            </span>
            <p className="problem-recap__text">{createdProject.problem_text}</p>
          </div>

          <section className="stack" aria-labelledby="roles-heading">
            <div>
              <h3 id="roles-heading" className="section-title">
                Sana Yardımcı Olacak Roller
              </h3>
              <p className="section-subtitle">
                İstediğin rolden başla. Herkesi ikna etmen gerekiyor.
              </p>
            </div>

            {createdProject.role_sessions.length === 0 ? (
              <EmptyState
                icon="🤔"
                title="Bu problem için uygun rol bulunamadı"
                message="Problemi biraz daha detaylandırıp tekrar deneyebilirsin."
                action={
                  <BigButton
                    variant="ghost"
                    onClick={() => setCreatedProject(null)}
                  >
                    ← Problemi düzenle
                  </BigButton>
                }
              />
            ) : (
              <ul className="role-grid">
                {createdProject.role_sessions.map((rs, idx) => (
                  <li key={rs.id} className="role-card" data-status={rs.status}>
                    <div className="role-card__number" aria-hidden="true">
                      {idx + 1}
                    </div>
                    <div className="role-card__icon" aria-hidden="true">
                      {personaIcon(rs)}
                    </div>
                    <h4 className="role-card__title">{rs.role_name}</h4>
                    <p className="role-card__focus">{rs.role_focus}</p>
                    <BigButton
                      variant="primary"
                      fullWidth
                      onClick={() =>
                        navigate(
                          `/projects/${createdProject.id}/roles/${rs.id}`,
                        )
                      }
                    >
                      Bu rolle başla →
                    </BigButton>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </AppLayout>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // Görünüm 1: Problem giriş formu
  // ──────────────────────────────────────────────────────────────
  return (
    <AppLayout
      title="Yeni Proje"
      subtitle="Mezuniyet projen için bir problem anlat. Yapay zekâ muhatapları önerecek."
      width="narrow"
    >
      <div className="stack">
        <div className="stepper" aria-label="İlerleme">
          <div className="step active">
            <span className="step-dot" aria-hidden="true">1</span>
            <span className="step-label">Problemi Anlat</span>
          </div>
          <div className="step-line" aria-hidden="true" />
          <div className="step">
            <span className="step-dot" aria-hidden="true">2</span>
            <span className="step-label">Muhatapları Seç</span>
          </div>
        </div>

        <div className="hero">
          <span className="hero__badge">✨ Adım 1 / 2</span>
          <h2 className="hero__title">Haydi Başlayalım!</h2>
          <p className="hero__subtitle">
            Mezuniyet projen için bir <strong>problem</strong> seç ve{' '}
            <strong>çözümünü</strong> anlat. Yapay zekâ, problemine en uygun
            muhatapları önerecek.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="stack" noValidate>
          <div className="form-field">
            <label htmlFor="problem" className="form-label">
              Problemin nedir? <span className="form-hint">(1–3 cümle)</span>
            </label>

            <textarea
              id="problem"
              className="form-textarea"
              value={problemText}
              onChange={(e) => setProblemText(e.target.value)}
              required
              minLength={MIN_PROBLEM_LENGTH}
              maxLength={MAX_PROBLEM_LENGTH}
              rows={5}
              placeholder="Örn: Okul kantininde yemekler çok pahalı, öğrenciler dışarıdan yemek getirmek zorunda kalıyor."
              disabled={submitting}
              aria-describedby="problem-counter problem-help"
            />

            <div className="row row--between text-xs text-muted">
              <span id="problem-help" className={`form-help ${trimmedLength >= MIN_PROBLEM_LENGTH ? 'is-ok' : ''}`}>
                {trimmedLength < MIN_PROBLEM_LENGTH
                  ? `En az ${MIN_PROBLEM_LENGTH} karakter yaz (${trimmedLength}/${MIN_PROBLEM_LENGTH})`
                  : '✓ Hazır!'}
              </span>
              <span
                id="problem-counter"
                className={`char-counter ${remaining < 50 ? 'is-warn' : ''} ${
                  remaining < 0 ? 'is-error' : ''
                }`}
                aria-live="polite"
              >
                {problemText.length}/{MAX_PROBLEM_LENGTH}
              </span>
            </div>
          </div>

          <fieldset className="example-chips" disabled={submitting}>
            <legend className="example-chips__legend">
              <span aria-hidden="true">💡</span> Fikir lazım mı? Bir örnekle başla:
            </legend>
            <div className="example-chips__list">
              {EXAMPLE_PROBLEMS.map((ex) => (
                <button
                  key={ex.text}
                  type="button"
                  className="example-chip"
                  onClick={() => setProblemText(ex.text)}
                >
                  <span className="example-chip__icon" aria-hidden="true">
                    {ex.icon}
                  </span>
                  <span className="example-chip__text">{ex.text}</span>
                </button>
              ))}
            </div>
          </fieldset>

          {error && (
            <div role="alert" className="error">
              <span aria-hidden="true">⚠️</span> {error}
            </div>
          )}

          {submitting ? (
            <LoadingDots message="Yapay zekâ düşünüyor" />
          ) : (
            <BigButton
              type="submit"
              variant="primary"
              icon="✨"
              fullWidth
              disabled={!canSubmit}
            >
              Projeyi Oluştur
            </BigButton>
          )}
        </form>
      </div>
    </AppLayout>
  );
}
